/**
 * features/recurring/RecurringManager.tsx
 * List recurring templates, flag the ones due, and generate the next
 * invoice on demand.
 */
import { useMemo, useState } from 'react';
import { RefreshCw, Plus, Play, Pause } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useClients } from '@/features/clients/useClients';
import { useRecurring } from './useRecurring';
import { RecurringEditor } from './RecurringEditor';
import { generateNow, setTemplateActive } from './recurringWrite';
import { buildLineItems, computeTotals, formatPula } from '@/lib/money';
import type { RecurringInvoice } from '@/types';
import { Button, Card, Badge, Spinner } from '@/components/ui/primitives';

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}
const FREQ_LABEL: Record<string, string> = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual' };

export function RecurringManager() {
  const { user } = useAuth();
  const { settings, currentVatSnapshot } = useSettings();
  const { clients } = useClients();
  const { templates, loading } = useRecurring();
  const [editor, setEditor] = useState<RecurringInvoice | null | 'new'>(null);
  const [running, setRunning] = useState<string | null>(null);
  const actor = user?.email ?? user?.uid ?? 'unknown';
  const today = new Date().toISOString().slice(0, 10);

  const clientName = useMemo(() => {
    const m = new Map(clients.map((c) => [c.id, c.name]));
    return (id: string) => m.get(id) ?? 'Unknown client';
  }, [clients]);

  async function runNow(t: RecurringInvoice) {
    const client = clients.find((c) => c.id === t.clientId);
    if (!client) {
      window.alert('Client not found for this template.');
      return;
    }
    if (!window.confirm(`Generate and issue an invoice for ${client.name} now?`)) return;
    setRunning(t.id);
    try {
      const { number } = await generateNow(t, client, currentVatSnapshot(), actor);
      window.alert(`Invoice ${number} created.`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not generate the invoice.');
    } finally {
      setRunning(null);
    }
  }

  function templateTotal(t: RecurringInvoice) {
    return computeTotals(buildLineItems(t.lineItems, currentVatSnapshot())).total;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          New recurring invoice
        </Button>
      </div>

      {loading ? (
        <Spinner label="Loading recurring invoices…" />
      ) : templates.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <RefreshCw size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">No recurring invoices yet. Set one up for fees you bill on a schedule.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Frequency</th>
                <th className="px-4 py-3 font-semibold">Next run</th>
                <th className="px-4 py-3 text-right font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => {
                const due = t.active && t.nextRunDate <= today;
                return (
                  <tr key={t.id} className="border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5">
                    <td className="cursor-pointer px-4 py-3 font-semibold text-primary dark:text-gray-100" onClick={() => setEditor(t)}>
                      {clientName(t.clientId)}
                    </td>
                    <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{FREQ_LABEL[t.frequency]}</td>
                    <td className="px-4 py-3">
                      <span className={due ? 'font-semibold text-amber-600 dark:text-amber-400' : 'text-primaryLight dark:text-gray-400'}>{fmtDate(t.nextRunDate)}</span>
                      {due && <span className="ml-2 align-middle"><Badge tone="warning">Due</Badge></span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-primary dark:text-gray-100">{formatPula(templateTotal(t))}</td>
                    <td className="px-4 py-3">
                      <Badge tone={t.active ? 'success' : 'neutral'}>{t.active ? 'Active' : 'Paused'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => void setTemplateActive(t.id, !t.active)}
                          title={t.active ? 'Pause' : 'Resume'}
                          className="rounded-lg p-1.5 text-primaryLight hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          {t.active ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={() => void runNow(t)} loading={running === t.id} disabled={!settings}>
                          Generate now
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && <RecurringEditor template={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />}
    </div>
  );
}
